<template>
  <div class="basecat1">
    <div>
      <br />

      <h2>Baseband Catalog 1 (Total: {{ numBaseCat1Bursts }} FRBs)</h2>
      <p>
        <b style="color:green">These data are provided to you by the CHIME/FRB collaboration. When
          using these data, please cite the <a href="https://ui.adsabs.harvard.edu/abs/2024ApJ...969..145T/abstract"
            target="_blank"> CHIME/FRB Baseband Catalog 1</a>
          and the <a href="https://ui.adsabs.harvard.edu/abs/2024arXiv240117378P/abstract" target="_blank"> CHIME/FRB
            Polarization Catalog 1</a> for polarization information.
          <br>Refer to CHIME/FRB Baseband Catalog 1 for a glossary of terms and units.</b>
      </p>

      <p style="color:green">
        <b>Access the CHIME/FRB Baseband Catalog 1 Public Data at the</b>
        <a href="https://doi.org/10.11570/23.0029" target="_blank" rel="noopener noreferrer"> Canadian Astronomy Data
          Centre</a>
        <b> (external website).</b>
      </p>

      <!-- Download Buttons -->
      <b-row>
        <b-col sm="6">
          <a class="download-button btn btn-dark" :href="links.catalog_csv" :download="'baseband_catalog_1.csv'"
            role="button">
            Baseband Catalog 1 as .CSV
          </a>
        </b-col>
        <b-col sm="6">
          <a class="download-button btn btn-dark" :href="links.catalog_json" :download="'baseband_catalog_1.json'"
            role="button">
            Baseband Catalog 1 as .JSON
          </a>
        </b-col>
      </b-row>
      <!-- Data table -->
      <b-container fluid>
        <b-card no-body>
          <b-tabs pills card fill>
            <b-card-text>
              <!-- Main table element -->
              <b-row>
                <b-col md="12" v-if="items.length != 0">
                  <b-table striped bordered outlined responsive sticky-header :items="items" :fields="fields"
                    :small="smallTable" :dark="false" :current-page="1" :per-page="600" :filter="filter"
                    :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :sort-direction="sortDirection"
                    :head-variant="headVariant" @filtered="onFiltered">
                  </b-table>
                </b-col>
              </b-row>
            </b-card-text>
            <template #tabs-end>
              <b-button class="mx-3 mb-auto" variant="success" v-b-modal.modal-1 align="center">Select
                Columns</b-button>
              <b-form-group horizontal>
                <b-input-group>
                  <b-form-input v-model="filter" placeholder="Type to Search TNS name" />
                  <b-input-group-append>
                    <b-btn :disabled="!filter" @click="filter = ''">Clear</b-btn>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </template>
          </b-tabs>
        </b-card>
      </b-container>
      <!-- Display Table Fields modal -->
      <b-modal @ok="updatecolumns" id="modal-1" scrollable centered size="lg" title="Select Columns">
        <b-form-group id="fieldform" label=" " v-slot="{ ariaDescribedby }">
          <b-form-checkbox v-for="option in columns" v-model="showColumns" :key="option.key" :value="option.label"
            :aria-describedby="ariaDescribedby" name="flavour-3a">
            <p>
              <code>{{ option.key }}</code>
            </p>
          </b-form-checkbox>
        </b-form-group>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { filterMixin } from './mixins/filter_mixin.js'
export default {
  mixins: [filterMixin],
  data() {
    return {
      items: [],
      numBaseCat1Bursts: 0,
      fields: [],
      columns: [],
      showColumns: [],
      smallTable: true,
      dark: false,
      fixed: true,
      currentPage: 1,
      perPage: 5,
      pageOptions1: [5, 10, 15, 50, 100, 150, 200],
      sortBy: null,
      plot: null,
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      headVariant: "dark",
      variants: [
        "primary",
        "secondary",
        "danger",
        "warning",
        "success",
        "info",
        "dark",
      ],
      checked: true,
      links: {
        catalog_csv: "https://storage.googleapis.com/chimefrb-dev.appspot.com/basecat1/basecat1_catalog.csv",
        catalog_json: "https://storage.googleapis.com/chimefrb-dev.appspot.com/basecat1/basecat1_catalog.json",
      },
    };
  },
  mounted() {
    var url = 'https://catalog.chime-frb.ca/baseband-catalog-1';
    this.$http
      .post(
        url,
        {
          responseType: "json",
          timeout: 10000,
        }
      )
      .then((response) => {
        this.parseResponse(response.body);
      })
  },
  methods: {
    parseResponse(events) {
      this.items = events.map(event => {
        // Round off certain parameters
        event['Min_width(s)'] = parseFloat(event['Min_width(s)']).toFixed(5);
        event['Duration(s)'] = parseFloat(event['Duration(s)']).toFixed(5);
        event['li'] = parseFloat(event['li']).toFixed(5);
        event['rm_qu'] = parseFloat(event['rm_qu']).toFixed(5);

        // if a certain value is -9999, replace it with 'N/A'
        for (var key in event) {
          if (event[key] == -9999) {
            event[key] = 'N/A';
          }
        }
        return event;
      });
      var columns = [
        'tns_name',
        'ra',
        'ra_err',
        'dec',
        'dec_err',
        'DM',
        'DM_err',
        'fluence',
        'fluence_err',
        'flux',
        'flux_err',
        'exp_up',
        'exp_up_err',
        'exp_low',
        'exp_low_err',
        'li',
        'li_err',
        'rm_fdf',
        'rm_fdf_err',
        'rm_qu',
        'rm_qu_err',
        'rm_mw',
        'rm_mw_err',
        'dm_mw',
        'pa_chi_squared',
        'depol_frac',
        'notes',
        'Time_res(s)',
        'Fitburst_DM',
        'Fitburst_DM_err',
        'Widths(s)',
        'Widths_err(s)',
        'Min_width(s)',
        'Min_width_err(s)',
        'Num_comps',
        'Duration(s)',
        'Rel_arrival_time(s)',
        'Scattering_600 MHz(ms)',
        'Scattering_err(ms)',
        'Bandwith (MHz)'
      ];
      var cols_to_show = [
        'tns_name',
        'ra',
        'ra_err',
        'dec',
        'dec_err',
        'DM',
        'fluence',
        'flux',
        'Min_width(s)',
        'Num_comps',
        'Duration(s)',
        'Scattering_600 MHz(ms)',
        'Bandwith (MHz)',
        'li',
        'rm_qu',
      ];
      for (var i = 0; i < columns.length; i++) {
        this.columns.push({
          key: columns[i],
          label: columns[i],
          sortable: true,
          class: "text-center",
        });
      }
      this.fields = [];
      this.fields.push({
        key: "tns_name",
        label: "tns_name",
        sortable: true,
        class: "text-center",
      });
      for (var i1 = 0; i1 < columns.length; i1++) {
        this.fields.push({
          key: cols_to_show[i1],
          label: cols_to_show[i1],
          sortable: true,
          class: "text-center",
        });
      }
      this.numBaseCat1Bursts = events.length;
      this.showColumns = cols_to_show;
    },
    updatecolumns() {
      const order = [
        'tns_name',
        'ra',
        'ra_err',
        'dec',
        'dec_err',
        'DM',
        'DM_err',
        'fluence',
        'fluence_err',
        'flux',
        'flux_err',
        'exp_up',
        'exp_up_err',
        'exp_low',
        'exp_low_err',
        'li',
        'li_err',
        'rm_fdf',
        'rm_fdf_err',
        'rm_qu',
        'rm_qu_err',
        'rm_mw',
        'rm_mw_err',
        'dm_mw',
        'pa_chi_squared',
        'depol_frac',
        'notes',
        'Time_res(s)',
        'Fitburst_DM',
        'Fitburst_DM_err',
        'Widths(s)',
        'Widths_err(s)',
        'Min_width(s)',
        'Min_width_err(s)',
        'Num_comps',
        'Duration(s)',
        'Rel_arrival_time(s)',
        'Scattering_600 MHz(ms)',
        'Scattering_err(ms)',
        'Bandwith (MHz)'
      ];
      let cols_to_show = this.showColumns;
      let position_of = {};
      for (let i = 0; i < cols_to_show.length; i++) {
        for (let j = 0; j < order.length; j++) {
          if (order[j] == cols_to_show[i]) {
            position_of[j] = order[j];
          }
        }
      }
      const cols = Object.values(
        Object.fromEntries(Object.entries(position_of).sort())
      );
      this.fields = [];
      this.fields.push({
        key: "tns_name",
        label: "tns_name",
        sortable: true,
        class: "text-center",
      });
      for (var i1 = 0; i1 < cols.length; i1++) {
        this.fields.push({
          key: cols[i1],
          label: cols[i1],
          sortable: true,
          class: "text-center",
        });
      }
      cols.shift();
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
  },
};
</script>

<style>
.supsub {
  position: absolute;
}

.subscript {
  display: block;
  position: relative;
  left: 2px;
  bottom: -18px;
}

.superscript {
  display: block;
  position: relative;
  left: 2px;
  top: 2px;
}

.download-button {
  display: flex;
  justify-content: center;
  padding: 5px;
  color: #fff;
  opacity: 0.87;
  text-align: center;
}
</style>
